import { motion } from "framer-motion";
import { ArrowRight, Zap, Calendar } from "lucide-react";
import { Button } from "../ui/button";

const BOOKING_URL = "https://agendamiento.reservo.cl/makereserva/agenda/v0XFJkK0p04Joq5h8h11dw79n3v0yK";

export const CTAFinalSection = () => {
  const whatsappUrl = `https://wa.me/56966951278?text=${encodeURIComponent("¡Hola! Quiero agendar mi clase de prueba en Inspirion GYM. ¡Estoy listo para empezar!")}`;

  return (
    <section 
      data-testid="cta-final-section"
      className="relative py-24 bg-black overflow-hidden"
    >
      {/* Animated background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-30"
          style={{
            background: "radial-gradient(circle, rgba(0, 255, 0, 0.4) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, rgba(0, 255, 255, 0.4) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
      </div>

      {/* Top accent with animation */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-neon-green via-neon-cyan to-neon-magenta"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.8 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 200 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-bold uppercase tracking-wider mb-8"
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Zap className="w-4 h-4" fill="currentColor" />
          </motion.div>
          Empieza hoy
        </motion.div>

        {/* Main Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight mb-6"
        >
          <motion.span
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="block"
          >
            Tu mejor versión
          </motion.span>
          <motion.span 
            className="block text-neon-green"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            animate={{
              textShadow: [
                "0 0 20px rgba(0,255,0,0.5)",
                "0 0 40px rgba(0,255,0,0.8)",
                "0 0 20px rgba(0,255,0,0.5)",
              ]
            }}
          >
            te está esperando
          </motion.span>
        </motion.h2>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-10"
        >
          No dejes para mañana lo que tu cuerpo necesita hoy. 
          Agenda tu clase gratis y descubre lo que el entrenamiento 
          personalizado puede hacer por ti.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              data-testid="cta-final-booking"
              className="group relative overflow-hidden bg-neon-green hover:bg-neon-green text-black font-bold text-lg px-10 py-7 rounded-none uppercase tracking-wide"
              style={{ boxShadow: "0 0 40px rgba(0, 255, 0, 0.5)" }}
            >
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <span className="relative z-10 flex items-center">
                  <Calendar className="mr-2 w-5 h-5" />
                  Agenda tu clase de prueba
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-white"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5 }}
                  style={{ opacity: 0.2 }}
                />
              </a>
            </Button>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              variant="outline"
              data-testid="cta-final-whatsapp"
              className="border-2 border-neon-whatsapp bg-transparent text-neon-whatsapp hover:bg-neon-whatsapp hover:text-black font-bold text-lg px-10 py-7 rounded-none uppercase tracking-wide transition-all duration-300"
            >
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                Consultar por WhatsApp
              </a>
            </Button>
          </motion.div>
        </motion.div>

        {/* Trust badges with animation */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="mt-12 flex flex-wrap justify-center gap-8 text-gray-500 text-sm"
        >
          {[
            { text: "Sin compromiso", color: "#00FF00" },
            { text: "Clase de prueba", color: "#00FFFF" },
            { text: "Resultados garantizados", color: "#FF00FF" },
          ].map((badge, index) => (
            <motion.span 
              key={index}
              className="flex items-center gap-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 + index * 0.1 }}
              whileHover={{ scale: 1.1, color: "#ffffff" }}
            >
              <motion.div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: badge.color }}
                animate={{ 
                  scale: [1, 1.5, 1],
                  opacity: [1, 0.5, 1]
                }}
                transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
              />
              {badge.text}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CTAFinalSection;
